import React, { useCallback, useEffect, useState } from 'react'
import ListItem, { BaseProps } from './ListItem'
import { ColInfo, ColPhoto, ColStat } from '../Col'
import styles from '../../../../CSS/main.css'
import classNames from 'classnames'
import Button from '../../Button/Button'
import { formatDate } from '../../../utils/date'
import { Routes, TypedLink } from '../../../routing'
import { AdminCommentsTemplateData, UserListData } from '../../../../../types'
import CommentActions from '../../../routes/Comments/CommentActions'
import { formatNumber } from '../../../utils/number'
import History from '../../History'
import { HistoryItem } from '../../History/types'
import { getCommentHistoriesById } from '../../../api/requests'

export enum UserStatus {
  Active = 'active',
  Pending = 'pending',
  Suspended = 'suspended',
  Paused = 'paused',
}

export enum AccountType {
  User = 'user',
  Moderator = 'moderator',
  Administrator = 'administrator',
}

export enum UserCommentModerationMode {
  ModerationRequired = 'moderation_required',
  AutomaticApproval = 'automatic_approval',
}

export type User = UserListData

export type FilterWords = {
  id: string
  forbiddenWord: string
}

export enum CommentStatus {
  Approved = 'approved',
  Blocked = 'blocked',
  Deleted = 'deleted',
  None = 'none',
}

export type Comment = AdminCommentsTemplateData

type Props = {
  readonly comment: Readonly<Comment>
  readonly onApprove: () => void
  readonly onBlock: () => void
  readonly isDeleted: boolean
} & BaseProps

const cssImage = (index: number) => {
  switch (index) {
    case 1:
      return styles.MediaCard1
    case 2:
      return styles.MediaCard2
    case 3:
      return styles.MediaCard3
    case 4:
      return styles.MediaCard4
    default:
      return styles.MediaCard1
  }
}

const CommentsItem: React.VFC<Props> = ({
  onSelected,
  selected,
  options,
  comment,
  onApprove,
  onBlock,
}) => {
  const [showMore, setShowMore] = useState<boolean>(false)
  const [commentHistory, setCommentHistory] = useState<
    ReadonlyArray<HistoryItem>
  >([])

  useEffect(() => {
    const getCommentHistoriesState = async (id: number) => {
      const commentHistories = await getCommentHistoriesById(id)
      setCommentHistory(commentHistories)
    }
    if (showMore) getCommentHistoriesState(comment.id)
  }, [comment.id, showMore])

  const toggleShowMore = useCallback(() => {
    setShowMore((prev) => !prev)
  }, [showMore])

  return (
    <ListItem
      onSelected={!comment.deleted ? onSelected : null}
      selected={selected}
      options={options}
      showMore={showMore}
      isCommentList>
      <ColPhoto>
        <div
          className={styles.Avatar}
          style={{ backgroundImage: `url(${comment.avatar})` }}
        />
      </ColPhoto>
      <ColInfo>
        <div className={styles.GroupButtonNotify}>
          {Boolean(comment.forbiddenCount) && (
            <Button
              onClick={null}
              additionalClassNames={[styles.ButtonForbidden]}>
              Restricted Word - {comment.forbiddenCount}
            </Button>
          )}
          {Boolean(comment.flagged) && (
            <Button
              onClick={null}
              additionalClassNames={[styles.ButtonWarning]}>
              Flagged
            </Button>
          )}
          {Boolean(comment.isSpam) && !Boolean(comment.forbiddenCount) && (
            <Button
              onClick={null}
              additionalClassNames={[styles.ButtonWarning]}>
              Spammed
            </Button>
          )}
        </div>

        <p className={styles.InfoSmall}>
          <TypedLink to={Routes.EditUser} params={{ id: comment.userId }}>
            {comment.displayName}
          </TypedLink>
          {comment.edited && <b dangerouslySetInnerHTML={{ __html: ' &bull; edited' }} />}
        </p>
        <p className={classNames([styles.InfoBig, styles.Ellipsis])}>
          <p dangerouslySetInnerHTML={{ __html: comment.comment }} />
        </p>
        {Boolean(comment.images.length) && (
          <div className={styles.Media}>
            {comment.images.map((url, index) => (
              <div
                className={`${styles.MediaCard} ${cssImage(
                  comment.images.length
                )}`}
                key={index}>
                <img src={url} alt='' />
              </div>
            ))}
          </div>
        )}
        {!comment.deleted && (
          <a href={`${comment.origin}#comment-${comment.id}`} target='_blank'>
            {comment.origin}
          </a>
        )}
        {!comment.deleted && (
          <>
            {showMore ? (
              <div className={styles.CommentInfo}>
                <p>Comment statistics:</p>
                <ul>
                  <li>
                    Likes: <b>{formatNumber(comment.likes)}</b>
                  </li>
                  <li>
                    Dislikes: <b>{formatNumber(comment.dislikes)}</b>
                  </li>
                  <li>
                    Flagged: <b>{formatNumber(comment.countFlag)}</b>
                  </li>
                </ul>
                <p>Comment history:</p>
                <History items={commentHistory} />
                <p className={styles.ToggleExpand} onClick={toggleShowMore}>
                  less
                </p>
              </div>
            ) : (
              <p className={styles.ToggleExpand} onClick={toggleShowMore}>
                more
              </p>
            )}
          </>
        )}
        {!comment.deleted && (
          <div className={styles.ButtonBlock}>
            <CommentActions
              comment={comment}
              onApprove={onApprove}
              onBlock={onBlock}
            />
          </div>
        )}
      </ColInfo>
      <ColStat>
        {formatDate(comment.createdAt, { format: 'MM/dd/yyyy' })}
      </ColStat>
    </ListItem>
  )
}

export default CommentsItem
